import React, { useEffect, useState } from "react";
import { dashboardLink } from "constants/Links";
import { Helmet } from "react-helmet-async";
import styled, { css } from "styled-components";
import { IBlog } from "types/interfaces/blog/IBlog";
import sanityClient, { sanityImgBuilder } from "./sanity";
import { useHistory, useLocation } from "react-router-dom";
import { device, size } from "constants/Devices";
import { format, parseISO } from "date-fns";
import Logger from "services/logger";
import Category from "./components/Category";
import ArticleContent from "./components/ArticleContent";
import { Typography } from "@beamcloud/design-system";
import Author from "./components/Author";
import RelatedBlogs from "./components/RelatedBlogs";
import BannerSection from "../components/BannerSection";
import { typography } from "../components/typography";
import { LinkMarketingButton } from "../styles";
import { Root as Portal } from "@radix-ui/react-portal";

interface IProps {}

const Article: React.FC<IProps> = () => {
  const history = useHistory();
  const location = useLocation();

  const [article, setArticle] = useState<IBlog>();

  useEffect(() => {
    const slug = location.pathname.replace(new RegExp("/+$"), "").split("/").pop();

    if (!slug) {
      return;
    }

    (async (blogSlug: string) => {
      try {
        const blog: IBlog = await sanityClient.fetch(
          `
          *[_type == "blog" && slug.current == "${blogSlug}"][0]{
            "id": _id,
            title,
            description,
            author->{
              "id": _id,
              name,
              "image": picture
            },
            categories,
            content,
            date,
            coverImage,
            "relatedBlogs": *[
              _type == "blog" && 
              count(categories[^._id != ^.^._id && @ in ^.^.categories[]]) > 0
            ] | order(_createdAt desc) [0..1] {
              "id": _id,
              title,
              description,
              author->{
                "id": _id,
                name,
                "image": picture
              },
              categories,
              coverImage,
              "slug": slug.current
            }
          }
        `
        );
        setArticle(blog);
      } catch (error) {
        Logger.error("fetchBlog", error);
      }
    })(slug);
  }, [location.pathname]);

  return (
    <Container>
      {article && (
        <>
          <Helmet>
            <title>{article.title} • Beam</title>
            <link rel="canonical" href={`${window.location.origin}${location.pathname}`} />
            <meta name="description" content={article.description} />
            <meta property="og:type" content="article" />
            <meta property="og:title" content={`${article.title} • Beam`} />
            <meta property="og:description" content={article.description} />
            <meta property="og:image" content={sanityImgBuilder.image(article.coverImage).url()} />
            <meta property="og:url" content={window.location.href} />
            <meta name="twitter:title" content={`${article.title} • Beam`} />
            <meta name="twitter:description" content={article.description} />
            <meta name="twitter:image" content={sanityImgBuilder.image(article.coverImage).url()} />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>
          <TitleWrapper>
            <Typography tag="h1" variant="title4Xl" color="gray100">
              {article.title}
            </Typography>
            <Info>
              <Author author={article.author} />
              <Typography tag="span" variant="textBase" color="gray300">
                {format(parseISO(article.date!), "MMMM d yyyy")}
              </Typography>
              <Categories>
                {article.categories.map((category, id) => (
                  <Category key={id} category={category} />
                ))}
              </Categories>
            </Info>
            <CoverImage src={sanityImgBuilder.image(article.coverImage).url()} />
          </TitleWrapper>
          <Content>
            <ArticleContent content={article.content!} />
          </Content>
          {article.relatedBlogs?.length ? <RelatedBlogs blogs={article.relatedBlogs} /> : <></>}
        </>
      )}
      <BannerSection />
      {/* <Portal>
        <FloatingCTA>
          <div>
            <h6>Try out Beam for free</h6>
            <p>Get started today with 10 hours of free credit</p>
          </div>
          <LinkMarketingButton href={dashboardLink}>Get Started</LinkMarketingButton>
        </FloatingCTA>
      </Portal> */}
    </Container>
  );
};

export default Article;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
  align-items: center;
  padding-top: 48px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  padding: 0px;
  gap: 32px;

  max-width: 800px;
  margin: 0 auto;

  @media ${device.mobileXL} {
    width: ${size.mobileS}px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 32px;
`;

const Categories = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
`;

const CoverImage = styled.img`
  border: 1px solid ${props => props.theme.colors.gray7};
  border-radius: 8px;
  max-width: calc(100% - 20px);
  max-height: 480px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  max-width: 670px;
  padding: 0 20px;
  gap: 16px;

  @media ${device.mobileXL} {
    width: ${size.mobileS}px;
  }
`;

const FloatingCTA = styled.div`
  ${({ theme }) => css`
    position: fixed;
    left: 16px;
    bottom: 16px;
    display: flex;
    max-width: 250px;
    padding: 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.gray6};
    background: rgba(30, 31, 36, 0.8);
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    backdrop-filter: blur(8px);

    & > div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      h6 {
        margin: unset;
        ${theme.styleSet.titleLg};
        color: ${theme.colors.gray12};
      }

      p {
        margin: unset;
        ${typography.textBaseLight};
        color: ${theme.colors.gray11};
      }
    }

    @media (max-width: ${theme.breakpoints.sm}px) {
      flex-direction: row;
      gap: 16px;
      padding: 12px 16px;
      align-items: center;

      & > div {
        h6 {
          ${theme.styleSet.titleBase};
        }

        p {
          display: none;
        }
      }
    }
  `}
`;
